import { assetCandidates, isPoisonedCacheEntry } from "@shared/chunkCache";

const HEAL_BUDGET_MS = 2_000;

/**
 * Before reloading over a failed chunk, overwrite any build file the browser
 * cached in a broken state. `/assets/*` is cached `immutable` for a year, so a
 * bad copy (the SPA shell stored under a chunk's name during a deploy rollout) is
 * read straight back from disk by every reload — including the error card's own
 * button — and the page stays broken until the seller clears their cache by hand.
 *
 * Only entries that are actually bad are refetched: each candidate is probed
 * from cache first. Never rejects, and gives up after HEAL_BUDGET_MS so a slow
 * network cannot hold the reload back.
 */
export function healCachedChunks(error: unknown): Promise<void> {
  const heal = (async () => {
    const message = error instanceof Error ? error.message : String(error ?? "");
    const pageUrls = [
      ...Array.from(
        document.querySelectorAll<HTMLLinkElement>('link[rel="modulepreload"], link[rel="stylesheet"]'),
        (link) => link.href,
      ),
      ...performance.getEntriesByType("resource").map((entry) => entry.name),
    ];
    const urls = assetCandidates(message, pageUrls, window.location.origin);
    await Promise.all(
      urls.map(async (url) => {
        try {
          const cached = await fetch(url, { cache: "only-if-cached", mode: "same-origin" });
          if (!isPoisonedCacheEntry(cached.status, cached.headers.get("content-type"))) return;
          // Read the body: the cache entry is only replaced once the download
          // completes, and the reload right after would abort a half-read one.
          await (await fetch(url, { cache: "reload" })).arrayBuffer();
        } catch {
          // No only-if-cached support, or offline — the reload still runs.
        }
      }),
    );
  })().catch(() => {});
  return Promise.race([heal, new Promise<void>((resolve) => setTimeout(resolve, HEAL_BUDGET_MS))]);
}
